import React, { useContext, useEffect, useMemo } from "react";
import clsx from "clsx";

import styles from "./ProductItem.module.css";
import ThemeContext from "~/contexts/ThemeContext";
import {
  ProductCardDescriptionFontSize,
  ProductCardImageBorderRadius,
  ProductCardImageSize,
  ProductCardPriceFontSize,
  ProductCardTitleFontSize,
} from "~/theme/models/themeModels";
import TaxContext from "~/contexts/TaxContext";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import { Product } from "~/entities/productEntities";

type ProductItemProps = {
  product: Product;
  onClick: VoidFunction;
};

const ProductItem: React.FC<ProductItemProps> = ({ product, onClick }) => {
  const [imageLoaded, setImageLoaded] = React.useState(false);

  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);

  const imageUrl = useMemo(() => {
    return product.image
      ? uploadcareConfig.getUrl(product.image.fileId, product.image.modifiers)
      : null;
  }, [product.image]);

  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      img.src = imageUrl;

      if (img.complete) {
        setImageLoaded(true);
      } else {
        img.onload = () => setImageLoaded(true);
        img.onerror = () => setImageLoaded(false);
      }

      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }
  }, [imageUrl]);

  // Styles
  const backgroundColor = template.menuScreen.card.backgroundColour;
  const borderColor = template.menuScreen.card.borderColour;
  const shadow = template.menuScreen.card.shadow;
  const titleFontSize =
    ProductCardTitleFontSize[template.menuScreen.card.titleFontSize];
  const titleFontColor = template.menuScreen.card.titleColour;
  const showDescription = template.menuScreen.card.showDescription;
  const descriptionFontSize =
    ProductCardDescriptionFontSize[
      template.menuScreen.card.descriptionFontSize
    ];
  const descriptionFontColor = template.menuScreen.card.descriptionColour;
  const priceFontSize =
    ProductCardPriceFontSize[template.menuScreen.card.priceFontSize];
  const priceFontColor = template.menuScreen.card.priceColour;
  const cardImageSize =
    ProductCardImageSize[template.menuScreen.card.imageSize];
  const cardImageBackgroundColor =
    template.menuScreen.card.imageBackgroundColour;
  const cardImageBorderRadius =
    ProductCardImageBorderRadius[template.menuScreen.card.imageShape];
  const soldOutStyles = {
    color: template.global.button.primaryButtonFontColour,
    backgroundColor: template.global.button.primaryButtonColour,
  };

  return (
    <div
      className={clsx(
        styles.wrapper,
        product.soldOut && styles.unavailable,
        shadow && styles.shadow
      )}
      style={{
        backgroundColor: product.soldOut
          ? `${backgroundColor}B2`
          : backgroundColor,
        borderColor: product.soldOut ? `${borderColor}B2` : borderColor,
      }}
      onClick={() => !product.soldOut && onClick()}
    >
      {product.soldOut && (
        <div className={styles.soldOutTag} style={soldOutStyles}>
          Sold out
        </div>
      )}
      <div className={styles.productDetails}>
        <span
          className={styles.productName}
          style={{ fontSize: titleFontSize, color: titleFontColor }}
        >
          {product.name}
        </span>
        {showDescription && (
          <span
            className={styles.productDescription}
            style={{
              color: descriptionFontColor,
              fontSize: descriptionFontSize,
            }}
          >
            {product.descr}
          </span>
        )}
        <div
          className={styles.productPrice}
          style={{ fontSize: priceFontSize, color: priceFontColor }}
        >
          {formatAmount(product.price)}
        </div>
      </div>
      <div
        className={styles.imageWrapper}
        style={{
          width: cardImageSize,
          height: cardImageSize,
          marginRight: `calc((142px - ${cardImageSize}) / 2)`,
          borderRadius: cardImageBorderRadius,
          backgroundColor: imageLoaded
            ? cardImageBackgroundColor
            : "transparent",
        }}
      >
        {product.image && !!imageUrl && (
          <>
            <img
              className={styles.image}
              src={imageUrl}
              alt={product.image.fileName}
              style={{ opacity: imageLoaded ? 1 : 0 }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ProductItem;
